// Các dòng của phiếu lương — dùng chung cho "Lương của tôi", bảng Tổng hợp của
// "Chi tiết lương" và phiếu in. Số chỉ đọc từ `payslipLines`, màn KHÔNG tự tính:
// ba màn tự xếp dòng từng in một khoản hai lần (bán thêm, Phụ phí, thưởng động
// lực) nên phiếu cộng không ra Thực lĩnh.
//
// Dòng "trong đó" thụt vào, chữ nhỏ, KHÔNG có dấu cộng/trừ — nó đã nằm trong
// số của dòng ngay trên.
import type { ReactNode } from "react";
import { vnd } from "../constants";
import type { PayrollRecord } from "../lib";
import { payslipLines, type SlipLine } from "../payslip-lines";
import { TrainingFeeRows } from "./TrainingFeeRows";

type Props = {
  record: PayrollRecord;
  /** "audit": lời giải thích phí đào tạo cho người soát lương. */
  variant?: "mine" | "audit";
  /** Ô hẹp của bảng chi tiết lương. */
  dense?: boolean;
  /** Tỉ lệ hoa hồng bán thêm ("5%") — ghi cạnh dòng "trong đó: Hoa hồng bán thêm". */
  upsellNote?: string;
};

// Khoản cộng/trừ NGOÀI công thức in kèm dấu "+", như phiếu cũ: người đọc thấy
// ngay đó là khoản cộng thêm chứ không phải một phần lương làm ra.
const SIGNED = new Set(["prevMonthAdjustment", "insurance", "bonusAmount", "customAdditions"]);

function money(l: SlipLine): string {
  if (l.amount < 0) return `− ${vnd(-l.amount)}`;
  return SIGNED.has(l.key) ? `+ ${vnd(l.amount)}` : vnd(l.amount);
}

export function SlipRows({ record, variant = "mine", dense = false, upsellNote }: Props) {
  const { main, within } = payslipLines(record);
  const pad = dense ? "px-1 py-1.5 md:px-2" : "px-2 py-1.5";
  // Dòng trừ góp vẽ bằng TrainingFeeRows (kèm kỳ, số còn lại) — một lần, ở chỗ
  // của dòng trừ góp đầu tiên.
  const firstFee = main.findIndex((l) => l.trainingFee);
  return (
    <>
      {main.map((l, i) => {
        if (l.trainingFee) {
          return i === firstFee ? (
            <TrainingFeeRows key="training-fees" record={record} variant={variant} dense={dense} />
          ) : null;
        }
        return (
          <SlipMainRow key={l.key} line={l} pad={pad}>
            {(within[l.key] ?? []).map((k) => (
              <tr key={`${l.key}:${k.key}`} data-testid={`slip-within-${k.key}`} className="text-[13px] text-muted-foreground">
                <td className={`${pad} pl-5 md:pl-6`}>
                  <span aria-hidden="true">↳ </span>
                  trong đó: {k.label}
                  {k.key === "upsellCommission" && upsellNote ? ` ${upsellNote}` : null}
                  {k.note ? <small className="block text-[12px] leading-snug">{k.note}</small> : null}
                </td>
                <td className={`${pad} text-right tabular-nums`}>{vnd(k.amount)}</td>
              </tr>
            ))}
          </SlipMainRow>
        );
      })}
    </>
  );
}

function SlipMainRow({ line, pad, children }: { line: SlipLine; pad: string; children: ReactNode }) {
  return (
    <>
      <tr data-testid={`slip-line-${line.key}`}>
        <td className={`${pad} text-muted-foreground`}>
          {line.label}
          {line.note ? <small className="block text-[12px] leading-snug">{line.note}</small> : null}
        </td>
        <td className={`${pad} text-right tabular-nums`}>{money(line)}</td>
      </tr>
      {children}
    </>
  );
}
